import React from 'react';
import {
  Banner,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  ErrorHandler,
  Flex,
  Gap,
  Gutter,
  InfoTooltip,
  InputNumber,
  Row,
  Select,
  Text,
  Form,
} from '@common/components';
import type {
  LoadBalancingScheduleShiftPreference,
  LoadBalancingShiftPatternPreference,
} from '@common/graphql';
import type { FullValidationErrorFragmentType } from '@common/graphql/fragments';
import type { ApolloError } from '@common/hooks';
import { datetime } from '@common/services/datetime';
import {
  ISO_DATE,
  SHIFT_PATTERN_OPTIONS,
  SHIFT_TYPE_OPTIONS,
  SKILLS_TEMPORARY_COPY,
  buildRoleOptions,
  isEndBeforeStart,
  isPast,
  shownSkillUuids,
  skillEditFromSelection,
  type WorkerProfileFormValues,
} from './_schedule-worker-profile-update-form-service';
import { ScheduleWorkerSkillsSelect } from '@load-balancing/components/schedule-worker-skills-select';
import {
  fteDecimalFromPercentage,
  ftePercentageFromDecimal,
} from '@load-balancing/services';

const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';

const toPickerDate = (iso: string) => (iso ? datetime(iso) : null);

export function ScheduleWorkerProfileUpdateForm({
  values,
  onChange,
  saveAttempted,
  serverErrors = [],
  datesDisabled = false,
  assignableRoles = [],
  chargeRole,
  scheduleGroupId,
  skillsEnabled = false,
  skillsLocked = false,
  loadedSkillUuids = null,
  skillsError,
  chargeRoleTerm,
  today,
}: {
  values: WorkerProfileFormValues;
  onChange: (patch: Partial<WorkerProfileFormValues>) => void;
  saveAttempted: boolean;
  serverErrors?: FullValidationErrorFragmentType[];
  datesDisabled?: boolean;
  assignableRoles?: readonly string[];
  chargeRole?: string;
  scheduleGroupId: string;
  skillsEnabled?: boolean;
  skillsLocked?: boolean;
  loadedSkillUuids?: string[] | null;
  skillsError?: ApolloError;
  chargeRoleTerm: string;
  today?: string;
}) {
  const roleOptions = buildRoleOptions(assignableRoles);
  const dateMissing = saveAttempted && !values.effectiveDate;
  const datePast =
    saveAttempted &&
    !!values.effectiveDate &&
    isPast(values.effectiveDate, today);
  const dateError = dateMissing
    ? 'Required'
    : datePast
      ? 'Cannot be in the past'
      : undefined;
  const endDateError =
    saveAttempted && isEndBeforeStart(values.effectiveDate, values.endDate)
      ? 'Cannot be before the effective date'
      : undefined;

  return (
    <Gap vertical size="md" align="stretch" fullWidth>
      <Text size="md" color="midnightRegular">
        Changes apply starting on the Effective Date and remain active until the
        End Date or a later profile change.
      </Text>

      {serverErrors.length > 0 && (
        <Banner type="error" showIcon>
          <Gap vertical size="xxs" align="stretch">
            {serverErrors.flatMap((error) =>
              error.fullErrors.map((message) => (
                <Text size="sm" key={`${error.attribute}-${message}`}>
                  {message}
                </Text>
              ))
            )}
          </Gap>
        </Banner>
      )}

      <Form layout="vertical">
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item
              label="Effective Date"
              required
              validateStatus={dateError ? 'error' : undefined}
              help={dateError}
            >
              <DatePicker
                value={toPickerDate(values.effectiveDate)}
                onChange={(date) =>
                  onChange({ effectiveDate: date?.format(ISO_DATE) ?? '' })
                }
                format={DATE_DISPLAY_FORMAT}
                placeholder={DATE_DISPLAY_FORMAT}
                disabled={datesDisabled}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="End Date"
              validateStatus={endDateError ? 'error' : undefined}
              help={endDateError}
            >
              <DatePicker
                value={toPickerDate(values.endDate)}
                onChange={(date) =>
                  onChange({ endDate: date?.format(ISO_DATE) ?? '' })
                }
                format={DATE_DISPLAY_FORMAT}
                placeholder="Ongoing"
                disabled={datesDisabled}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider secondary />
        <Gutter direction="top" size="lg">
          <Gutter direction="bottom" size="sm">
            <Text size="lg" weight="medium" block>
              Profile
            </Text>
          </Gutter>
        </Gutter>
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item label="Role">
              <Select<string>
                value={values.role || undefined}
                onChange={(value) => onChange({ role: value })}
                options={roleOptions}
                placeholder="Select role"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Full-Time Equivalent (FTE)">
              <InputNumber
                value={
                  values.ftePercentage === null
                    ? null
                    : fteDecimalFromPercentage(values.ftePercentage)
                }
                onChange={(value) =>
                  onChange({
                    ftePercentage:
                      value === null
                        ? null
                        : ftePercentageFromDecimal(Number(value)),
                  })
                }
                min={0}
                max={1}
                step={0.1}
                placeholder="e.g. 0.9"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Gap vertical size="sm" align="stretch">
          {values.role !== chargeRole && (
            <Checkbox
              checked={values.chargeEligible}
              onChange={(e) => onChange({ chargeEligible: e.target.checked })}
            >
              {`${chargeRoleTerm} eligible`}
            </Checkbox>
          )}
          <Gap vertical size="xxs" align="stretch">
            <Flex align="center">
              <Checkbox
                checked={values.accountActive}
                onChange={(e) => onChange({ accountActive: e.target.checked })}
              >
                Include in scheduling
              </Checkbox>
              <InfoTooltip
                size="sm"
                iconColor="midnightLighter"
                leftOffset="none"
                title="Excluded dates aren’t counted toward FTE targets. Use this when a staff member should remain active but be excluded from scheduling for dates that aren’t already represented on the schedule, such as orientation or leave that isn’t otherwise captured."
              />
            </Flex>
            <Text size="sm" color="midnightLighter">
              Uncheck to exclude this staff member from scheduling for the
              selected dates. This does not deactivate their account.
            </Text>
          </Gap>
        </Gap>

        {skillsEnabled && (
          <>
            <Divider secondary gutter={24} />

            <Gutter direction="bottom" size="sm">
              <Text size="lg" weight="medium" block>
                Skills
              </Text>
            </Gutter>
            <Form.Item
              extra={
                skillsLocked ? (
                  <Text size="md" color="midnightRegular">
                    {SKILLS_TEMPORARY_COPY}
                  </Text>
                ) : undefined
              }
            >
              {skillsError ? (
                <ErrorHandler
                  error={skillsError}
                  log="ScheduleWorkerProfileUpdateForm"
                  messageType="contextual"
                />
              ) : (
                <ScheduleWorkerSkillsSelect
                  value={
                    skillsLocked
                      ? (loadedSkillUuids ?? [])
                      : shownSkillUuids({
                          loaded: loadedSkillUuids,
                          added: values.addedSkillUuids,
                          removed: values.removedSkillUuids,
                        })
                  }
                  onChange={(selected) =>
                    loadedSkillUuids &&
                    onChange(
                      skillEditFromSelection({
                        loaded: loadedSkillUuids,
                        selected,
                        added: values.addedSkillUuids,
                        removed: values.removedSkillUuids,
                      })
                    )
                  }
                  scheduleGroupId={scheduleGroupId}
                  disabled={skillsLocked || loadedSkillUuids === null}
                  loading={loadedSkillUuids === null}
                />
              )}
            </Form.Item>
          </>
        )}

        <Divider secondary gutter={24} />

        <Gutter direction="bottom" size="sm">
          <Text size="lg" weight="medium" block>
            Preferences
          </Text>
        </Gutter>
        <Form.Item label="Shift type">
          <Select<LoadBalancingScheduleShiftPreference>
            value={values.preferredTime}
            onChange={(value) => onChange({ preferredTime: value })}
            options={SHIFT_TYPE_OPTIONS}
          />
        </Form.Item>
        <Form.Item label="Shift pattern">
          <Select<LoadBalancingShiftPatternPreference>
            value={values.shiftPatternPreference}
            onChange={(value) => onChange({ shiftPatternPreference: value })}
            options={SHIFT_PATTERN_OPTIONS}
          />
        </Form.Item>
      </Form>
    </Gap>
  );
}
